import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthUser } from '../types';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  setUser: (user: AuthUser) => void;
  /** Merge fresh company fields (currency/timezone) into the live session so
   *  formatting updates after a Settings save without a re-login. */
  updateCompany: (company: Partial<NonNullable<AuthUser['company']>>) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      setUser: (user) => {
        console.log('🔐 [Auth Store] Setting user:', user);

        // Compute active branch based on priority: managedBranch > branch > defaultBranch
        const activeBranch = user.managedBranch || user.branch || user.defaultBranch || null;

        console.log('🏢 [Auth Store] Branch resolution:', {
          hasManagedBranch: !!user.managedBranch,
          managedBranchId: user.managedBranch?.id,
          managedBranchName: user.managedBranch?.name,
          hasBranch: !!user.branch,
          branchId: user.branch?.id,
          branchName: user.branch?.name,
          hasDefaultBranch: !!user.defaultBranch,
          defaultBranchId: user.defaultBranch?.id,
          defaultBranchName: user.defaultBranch?.name,
          finalActiveBranchId: activeBranch?.id,
          finalActiveBranchName: activeBranch?.name,
        });

        if (!activeBranch) {
          console.warn('⚠️ [Auth Store] No active branch assigned to user! This will cause empty pages.');
          console.warn('⚠️ [Auth Store] User should have either managedBranch or branch assigned.');
        } else {
          console.log('✅ [Auth Store] Active branch set successfully:', {
            id: activeBranch.id,
            name: activeBranch.name,
          });
        }

        set({
          user: { ...user, activeBranch },
          isAuthenticated: true
        });

        console.log('✅ [Auth Store] User state updated. isAuthenticated:', true);
      },
      updateCompany: (company) => {
        set((state) => {
          if (!state.user) return state;
          return {
            user: {
              ...state.user,
              company: { ...state.user.company, ...company } as AuthUser['company'],
            },
          };
        });
      },
      logout: () => {
        console.log('🚪 [Auth Store] Logging out user');
        set({ user: null, isAuthenticated: false });
        console.log('✅ [Auth Store] User logged out. isAuthenticated:', false);
      },
    }),
    {
      name: 'auth-storage',
    }
  )
);
